import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { addToCart, getToken, removeToken, removeUser, addToWishlist, removeFromWishlist, getWishlist } from '../api/auth';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TAG_COLORS = {
  Trending:   { bg: '#B08D57', color: '#fff' },
  Limited:    { bg: '#1C1A18', color: '#D4AF78' },
  New:        { bg: '#3D3A36', color: '#fff' },
  Hot:        { bg: '#C0392B', color: '#fff' },
  Bestseller: { bg: '#4A3728', color: '#D4AA70' },
  Sale:       { bg: '#C0392B', color: '#FAF8F4' },
  Exclusive:  { bg: '#1C1A18', color: '#D4AA70' },
};

const getProductImages = (product) => {
  const fields = [product.image_1, product.image_2, product.image_3].filter(Boolean);
  const jsonImages = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  return [...new Set([...fields, ...jsonImages])];
};

const ImageSwiper = ({ product }) => {
  const imgs = getProductImages(product);
  const count = imgs.length;
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);

  const go = (dir, e) => {
    e.stopPropagation();
    setCurrent(c => (c + dir + count) % count);
  };

  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1, e);
    touchStartX.current = null;
  };

  return (
    <div className="vf-slide-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="vf-slide-strip" style={{ transform: `translateX(-${current * 100}%)` }}>
        {imgs.map((src, idx) => (
          <img key={idx} src={src} alt={product.name} loading="lazy" />
        ))}
      </div>
      {count > 1 && (
        <>
          <button className="vf-sarrow vf-sarrow-l" onClick={e => go(-1, e)} aria-label="Previous image">←</button>
          <button className="vf-sarrow vf-sarrow-r" onClick={e => go(1, e)} aria-label="Next image">→</button>
        </>
      )}
    </div>
  );
};

const SORT_OPTIONS = [
  { value: 'featured',   label: 'Featured' },
  { value: 'price-asc',  label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'discount',   label: 'Best Discount' },
  { value: 'newest',     label: 'Newest First' },
];

/* ── Sort utility ── */
const sortProducts = (arr, sortBy) => {
  const list = [...arr];
  switch (sortBy) {
    case 'price-asc':
      return list.sort((a, b) => Number(a.sale_price || a.original_price) - Number(b.sale_price || b.original_price));
    case 'price-desc':
      return list.sort((a, b) => Number(b.sale_price || b.original_price) - Number(a.sale_price || a.original_price));
    case 'discount':
      return list.sort((a, b) => {
        const dA = a.sale_price ? Math.round((1 - Number(a.sale_price) / Number(a.original_price)) * 100) : 0;
        const dB = b.sale_price ? Math.round((1 - Number(b.sale_price) / Number(b.original_price)) * 100) : 0;
        return dB - dA;
      });
    case 'newest':
      return list.sort((a, b) => (b.id || 0) - (a.id || 0));
    default:
      return list;
  }
};

/* ── Cart Icon SVG ── */
const CartIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);

/* ── Single deal card ── */
const DealCard = ({ product, index, collectionName, collectionSlug, wished, onToggleWishlist }) => {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [added, setAdded] = useState(false);
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  const hasDiscount  = product.sale_price && Number(product.sale_price) < Number(product.original_price);
  const displayPrice = hasDiscount ? Number(product.sale_price)     : Number(product.original_price);
  const mrpPrice     = hasDiscount ? Number(product.original_price) : null;
  const discount     = mrpPrice ? Math.round((1 - displayPrice / mrpPrice) * 100) : 0;

  const badge = product.badge || (discount >= 40 ? 'Hot' : discount >= 30 ? 'Trending' : 'New');
  const tc    = TAG_COLORS[badge] || TAG_COLORS['Hot'];

  const handleNavigate = () => navigate(`/products/${product.id}`, { state: { collectionName, collectionSlug } });
  

  const handleAdd = async (e) => {
  e.stopPropagation();
  if (!getToken()) { navigate('/account'); return; }

  const colorCount = [...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 || sizeCount > 1) {
    navigate(`/products/${product.id}`);
    return;
  }

  try {
    await addToCart(product.id, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
    window.dispatchEvent(new Event('cart-updated'));
  } catch (err) {
    const msg = err.message?.toLowerCase() || '';
    if (msg.includes('invalid') || msg.includes('expired') || msg.includes('session') || msg.includes('token') || msg.includes('unauthorized')) {
      removeToken(); removeUser(); navigate('/account');
    }
  }
};
  return (
    <motion.div
      ref={ref}
      className="vf-card"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: (index % 4) * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={handleNavigate}
    >
      <div className="vf-img-wrap">
        <ImageSwiper product={product} />
        <button
          className={`vf-heart${wished ? ' vf-heart--on' : ''}`}
          onClick={e => { e.stopPropagation(); onToggleWishlist(); }}
          aria-label={wished ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg viewBox="0 0 24 24" fill={wished ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l7.78-7.78a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
        <span className="vf-tag" style={{ background: tc.bg, color: tc.color }}>{badge}</span>
        {product.stock_qty === 0 && (
          <span style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(28,26,24,0.82)', color: '#fff',
            fontSize: 8, fontWeight: 700, letterSpacing: '0.18em',
            textTransform: 'uppercase', padding: '6px', textAlign: 'center', zIndex: 5,
          }}>Sold Out</span>
        )}
      </div>
      <div className="vf-body">
        {product.category && <p className="vf-category">{product.category}</p>}
        <h3 className="vf-name">{product.name}</h3>
        <div className="vf-prices">
          <span className="vf-price">{formatPrice(displayPrice)}</span>
          {mrpPrice && <span className="vf-original">{formatPrice(mrpPrice)}</span>}
          {discount > 0 && <span className="vf-discount">{discount}% Off</span>}
        </div>
        <button
          className={`vf-atc${added ? ' added' : ''}`}
          onClick={handleAdd}
          disabled={product.stock_qty === 0}
          style={product.stock_qty === 0 ? { background: '#9A9186', cursor: 'not-allowed' } : {}}
        >
          {added ? '✓ Added to Cart'
            : product.stock_qty === 0 ? 'Sold Out'
            : ([...new Set(product.colors?.filter(c => c && c.trim()) ?? [])].length > 1 ||
            [...new Set(product.sizes?.filter(s => s && s.trim()) ?? [])].length > 1) ? 'Select Options'
            : 'Add to Cart'}
        </button>
      </div>
    </motion.div>
  );
};

/* ── Skeleton card ── */
const SkeletonCard = () => (
  <div className="vf-card vf-skeleton">
    <div className="vf-img-wrap vf-skel-img" />
    <div className="vf-body">
      <div className="vf-skel-line short" />
      <div className="vf-skel-line" />
      <div className="vf-skel-line medium" />
      <div className="vf-skel-btn" />
    </div>
  </div>
);

/* ── Main Component ── */
const ViralFinds = () => {
  const [products, setProducts]         = useState([]);
  const [filtered, setFiltered]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [categories, setCategories]     = useState(['All']);
  const [sortBy, setSortBy]             = useState('featured');
  const [wishlist, setWishlist]         = useState(new Set());

  const heroRef  = useRef(null);
  const heroView = useInView(heroRef, { once: true });
  const navigate = useNavigate();

  const COLLECTION_SLUG = 'viral-finds';
  const COLLECTION_NAME = 'Hot Deals';

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`${API}/products?collection=${COLLECTION_SLUG}&active=true`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const prods = data.products || [];
          setProducts(prods);
          setFiltered(sortProducts(prods, 'featured'));
          const cats = ['All', ...new Set(prods.map(p => p.category).filter(Boolean))];
          setCategories(cats);
        } else {
          setError(data.error || 'Failed to load products.');
        }
      })
      .catch(() => setError('Failed to connect to server.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    getWishlist()
      .then(items => setWishlist(new Set(items.map(item => item.id))))
      .catch(() => {});
  }, []);

  const handleWishlist = async (productId) => {
    if (!getToken()) { navigate('/account'); return; }
    const isIn = wishlist.has(productId);
    setWishlist(prev => {
      const set = new Set(prev);
      isIn ? set.delete(productId) : set.add(productId);
      return set;
    });
    try {
      if (isIn) await removeFromWishlist(productId);
      else await addToWishlist(productId);
    } catch {
      setWishlist(prev => {
        const set = new Set(prev);
        isIn ? set.add(productId) : set.delete(productId);
        return set;
      });
    }
  };

  const applyFilterSort = (cat, sort) => {
    const base = cat === 'All' ? products : products.filter(p => p.category === cat);
    setFiltered(sortProducts(base, sort));
  };

  const handleFilter = (cat) => { setActiveFilter(cat); applyFilterSort(cat, sortBy); };
  const handleSort   = (e)   => { const s = e.target.value; setSortBy(s); applyFilterSort(activeFilter, s); };
  

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        .vf-page {
          background: #FAF8F4;
          min-height: 100vh;
          padding-top: 162px;
          font-family: 'Jost', sans-serif;
          position: relative;
          overflow-x: hidden;
        }
        .vf-page::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        /* Hero */
        .vf-hero {
          position: relative; z-index: 1; text-align: center;
          padding: 56px clamp(20px, 5vw, 80px) 50px;
          border-bottom: 1px solid rgba(176,141,87,0.15);
          background: linear-gradient(to bottom, #F0EDE6, #FAF8F4);
        }
        .vf-hero-eyebrow {
          font-size: 10px; font-weight: 500; letter-spacing: 0.35em;
          text-transform: uppercase; color: #B08D57;
          display: flex; align-items: center; justify-content: center;
          gap: 14px; margin-bottom: 20px;
        }
        .vf-hero-eyebrow::before, .vf-hero-eyebrow::after {
          content: ''; width: 40px; height: 1px;
          background: linear-gradient(to right, transparent, #B08D57);
        }
        .vf-hero-eyebrow::after { background: linear-gradient(to left, transparent, #B08D57); }
        .vf-hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(38px, 6vw, 72px); font-weight: 700;
          color: #1C1A18; line-height: 1.0; letter-spacing: -0.02em; margin-bottom: 8px;
        }
        .vf-hero-title em { font-style: italic; font-weight: 400; color: #B08D57; display: block; }
        .vf-hero-desc {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(15px, 1.8vw, 19px); font-style: italic;
          font-weight: 300; color: #8A7E72; letter-spacing: 0.03em; margin-top: 12px;
        }
        .vf-hero-tagline {
          font-family: 'Jost', sans-serif; font-size: 12px; font-weight: 400;
          letter-spacing: 0.16em; text-transform: uppercase; color: #B08D57; margin-top: 24px;
        }

        /* Inner */
        .vf-inner {
          position: relative; z-index: 1; max-width: 1280px;
          margin: 0 auto; padding: 0 clamp(16px, 4vw, 64px) 100px;
        }

        /* Filter bar */
        .vf-filters {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
          margin: 44px 0 0; padding-bottom: 20px;
          border-bottom: 1px solid rgba(176,141,87,0.15);
        }
        .vf-filter-label {
          font-size: 10px; font-weight: 500; letter-spacing: 0.22em;
          text-transform: uppercase; color: #9A9186; margin-right: 8px;
        }
        .vf-filter-btn {
          font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 500;
          letter-spacing: 0.14em; text-transform: uppercase; background: none;
          border: 1px solid #e0d8cc; color: #6b6560; padding: 8px 18px;
          cursor: pointer; transition: all 0.2s; white-space: nowrap;
        }
        .vf-filter-btn:hover  { border-color: #B08D57; color: #B08D57; }
        .vf-filter-btn.active { background: #1C1A18; border-color: #1C1A18; color: #fff; }

        /* ── Sort bar ── */
        .vf-sort-bar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 0; margin-top: 20px; margin-bottom: 28px;
          border-bottom: 1px solid rgba(176,141,87,0.12);
          gap: 12px; flex-wrap: wrap;
        }
        .vf-showing {
          font-size: 12px; color: #9A9186; letter-spacing: 0.04em;
        }
        .vf-showing strong { color: #1C1A18; font-weight: 600; }
        .vf-sort-right { display: flex; align-items: center; gap: 12px; }
        .vf-sort-label {
          font-size: 10px; font-weight: 500; letter-spacing: 0.22em;
          text-transform: uppercase; color: #9A9186;
        }
        .vf-sort-select {
          font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500;
          letter-spacing: 0.06em; color: #1C1A18; background: #fff;
          border: 1px solid #e0d8cc; padding: 9px 36px 9px 14px;
          cursor: pointer; outline: none; appearance: none; -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23B08D57' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 12px center;
          min-width: 165px; transition: border-color 0.2s;
        }
        .vf-sort-select:hover,
        .vf-sort-select:focus { border-color: #B08D57; }
        .vf-cart-btn {
          display: flex; align-items: center; gap: 8px;
          font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 600;
          letter-spacing: 0.18em; text-transform: uppercase;
          background: #1C1A18; color: #fff; border: none;
          padding: 9px 20px; cursor: pointer; transition: background 0.22s; white-space: nowrap;
        }
        .vf-cart-btn:hover { background: #B08D57; }
        .vf-cart-badge {
          background: #B08D57; color: #fff; font-size: 9px; font-weight: 700;
          min-width: 18px; height: 18px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
        }

        /* Grid */
        .vf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 20px; }

        /* Card */
        .vf-card { background: #fff; position: relative; transition: box-shadow 0.3s; }
        .vf-card:hover { box-shadow: 0 16px 48px rgba(28,26,24,0.10); }

        .vf-img-wrap {
          position: relative; width: 100%; height: 260px;
          overflow: hidden; background: #f5f0ea;
        }
        .vf-slide-wrap { width: 100%; height: 100%; position: relative; overflow: hidden; }
        .vf-slide-strip {
          display: flex; width: 100%; height: 100%; transition: transform 0.35s ease;
        }
        .vf-slide-strip img {
          flex: 0 0 100%; width: 100%; height: 100%; object-fit: cover; object-position: center top;
        }
        .vf-card:hover .vf-slide-strip img { transform: scale(1.05); }

        .vf-sarrow {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 32px; height: 32px; border: none;
          background: rgba(255,255,255,0.9); color: #1C1A18;
          font-size: 16px; cursor: pointer; display: flex;
          align-items: center; justify-content: center; z-index: 4;
          border-radius: 50%; transition: all 0.2s; opacity: 0;
        }
        .vf-img-wrap:hover .vf-sarrow,
        .vf-card:hover .vf-sarrow {
          opacity: 1;
        }
        .vf-sarrow:hover { background: #fff; }
        .vf-sarrow-l { left: 10px; }
        .vf-sarrow-r { right: 10px; }

        .vf-heart {
          position: absolute; top: 12px; right: 12px; z-index: 5;
          background: rgba(255,255,255,0.92); border: none; width: 36px; height: 36px;
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: transform 0.2s, background 0.2s;
        }
        .vf-heart:hover { transform: scale(1.05); background: #fff; }
        .vf-heart svg { width: 16px; height: 16px; color: #9A9186; transition: color 0.2s; }
        .vf-heart--on svg { color: #C0392B; }

        .vf-tag {
          position: absolute; top: 12px; left: 12px;
          font-family: 'Jost', sans-serif; font-size: 9px; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase; padding: 6px 12px; z-index: 5;
          border-radius: 999px;
        }

        .vf-body { padding: 14px 16px 18px; border-top: 1px solid #f5f0e8; }
        .vf-category {
          font-size: 9px; font-weight: 500; letter-spacing: 0.22em;
          text-transform: uppercase; color: #B08D57; margin-bottom: 4px;
        }
        .vf-name {
          font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 600;
          color: #1C1A18; margin-bottom: 10px; line-height: 1.3; cursor: pointer; transition: color 0.2s;
        }
        .vf-name:hover { color: #B08D57; }
        .vf-prices { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
        .vf-price    { font-family: 'Jost', sans-serif; font-size: 15px; font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
        .vf-original { font-size: 12px; color: #b0a99e; text-decoration: line-through; }
        .vf-discount {
          font-size: 10px; font-weight: 600; color: #C0392B;
          background: rgba(192,57,43,0.1); padding: 4px 8px; border-radius: 99px;
        }
        .vf-atc {
          width: 100%; background: #1C1A18; border: none; color: #fff;
          font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 600;
          letter-spacing: 0.18em; text-transform: uppercase; padding: 12px;
          cursor: pointer; transition: background 0.22s; display: block;
        }
        .vf-atc:hover { background: #B08D57; }
        .vf-atc.added { background: #8A6D3E; letter-spacing: 0.1em; }

        /* Skeleton */
        .vf-skel-img, .vf-skel-line, .vf-skel-btn {
          background: linear-gradient(90deg, #f0ebe3 25%, #e8e1d8 50%, #f0ebe3 75%);
          background-size: 200% 100%; animation: vf-shimmer 1.4s infinite;
        }
        .vf-skel-line { height: 12px; border-radius: 2px; margin-bottom: 10px; width: 100%; }
        .vf-skel-line.short  { width: 40%; }
        .vf-skel-line.medium { width: 65%; }
        .vf-skel-btn { height: 36px; border-radius: 1px; }
        @keyframes vf-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .vf-error { text-align: center; padding: 80px 20px; color: #C0392B; font-size: 13px; }
        .vf-empty { text-align: center; padding: 80px 20px; color: #9A9186; grid-column: 1 / -1; }
        .vf-empty-title { font-family: 'Playfair Display', serif; font-size: 22px; color: #3a3530; margin-bottom: 8px; }

        /* Ornament */
        .vf-ornament {
          display: flex; align-items: center; justify-content: center;
          gap: 16px; margin-top: 64px; opacity: 0.35;
        }
        .vf-ornament::before { content: ''; width: 80px; height: 1px; background: linear-gradient(to right, transparent, #B08D57); }
        .vf-ornament::after  { content: ''; width: 80px; height: 1px; background: linear-gradient(to left,  transparent, #B08D57); }
        .vf-diamond { width: 6px; height: 6px; background: #B08D57; transform: rotate(45deg); }

        /* Responsive */
        @media (max-width: 1024px) { .vf-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 860px) {
          .vf-page  { padding-top: 105px; }
          .vf-grid  { grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .vf-inner { padding: 0 16px 60px; }
          .vf-sort-label { display: none; }
          .vf-img-wrap { height: 200px; }
        }
        @media (max-width: 480px) {
          .vf-grid { gap: 10px; }
          .vf-sort-select { min-width: 120px; }
          .vf-img-wrap { height: 170px; }
        }
      `}</style>

      <div className="vf-page">

        {/* Hero */}
        <div className="vf-hero" ref={heroRef}>
          <motion.p className="vf-hero-eyebrow"
            initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }}>
            Aamira Jewellery
          </motion.p>
          <motion.h1 className="vf-hero-title"
            initial={{ opacity: 0, y: 24 }} animate={heroView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.12 }}>
            Hot<em>Deals</em>
          </motion.h1>
          <motion.p className="vf-hero-desc"
            initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.24 }}>
            Trending styles everyone's talking about — shop before they sell out.
          </motion.p>
          <motion.p className="vf-hero-tagline"
            initial={{ opacity: 0 }} animate={heroView ? { opacity: 1 } : {}} transition={{ duration: 0.6, delay: 0.36 }}>
            ✦ &nbsp; Handpicked. Trending. Only at Aamira.
          </motion.p>
        </div>

        <div className="vf-inner">

          {/* Category Filters */}
          {categories.length > 1 && (
            <div className="vf-filters">
              <span className="vf-filter-label">Filter</span>
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`vf-filter-btn${activeFilter === cat ? ' active' : ''}`}
                  onClick={() => handleFilter(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* ── Sort Bar: Showing X products | SORT dropdown | CART ── */}
          {!loading && !error && (
            <div className="vf-sort-bar">
              <p className="vf-showing">
                Showing <strong>{filtered.length} product{filtered.length !== 1 ? 's' : ''}</strong>
              </p>
              <div className="vf-sort-right">
                <span className="vf-sort-label">Sort:</span>
                <select className="vf-sort-select" value={sortBy} onChange={handleSort}>
                  {SORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                
              </div>
            </div>
          )}

          {/* Error */}
          {error && <div className="vf-error">{error}</div>}

          {/* Grid */}
          <div className="vf-grid">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
              : filtered.length === 0 && !error
                ? (
                  <div className="vf-empty">
                    <div className="vf-empty-title">No products yet</div>
                    <p>This collection is coming soon.</p>
                  </div>
                )
                : filtered.map((product, i) => (
                  <DealCard
                    key={product.id}
                    product={product}
                    index={i}
                    collectionName={COLLECTION_NAME}
                    collectionSlug={COLLECTION_SLUG}
                    wished={wishlist.has(product.id)}
                    onToggleWishlist={() => handleWishlist(product.id)}
                  />
                ))
            }
          </div>

          {/* Ornament */}
          <motion.div className="vf-ornament"
            initial={{ opacity: 0 }} whileInView={{ opacity: 0.35 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <div className="vf-diamond" />
          </motion.div>

        </div>
      </div>
    </>
  );
};

export default ViralFinds;